import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import "../stylesheets/AccessModal.scss"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"

const AccessModal = ({
  open,
  onRequestAccess,
  onLearnMore,
  contactHref,
  learnMoreHref,
  scoped,
  texts,
  translationKey,
}) => {
  const dialogRef = useRef(null)
  const { t } = useTranslation()
  const { currentOrganization, currentUser } = useAppContext()

  // Focus on opening and ESC management if onClose is provided
  useEffect(() => {
    if (!open) return
    dialogRef.current?.focus()
    const onKeyDown = (e) => {
      if (e.key === "Escape" && typeof onRequestAccess === "function") {
        // No forced closure by default for a paywall; access request can be triggered if desired
        // Leave blank by default to avoid side effects
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open, onRequestAccess])

  if (!open) return null

  const handleSecondary = (e) => {
    if (learnMoreHref) return
    e.preventDefault()
    if (typeof onLearnMore === "function") onLearnMore()
  }

  const tKey = translationKey ?? "explore:AccesModal"

  const resolvedTitle = texts?.title ?? t(`${tKey}.title`)
  const resolvedDescription =
    texts?.description ?? t(`${tKey}.description`)
  const resolvedPrimaryButton =
    texts?.primaryButton ?? t(`${tKey}.primaryButton`)
  const resolvedSecondaryButton =
    texts?.secondaryButton ?? t(`${tKey}.secondaryButton`)

  const resolvedMailSubject =
    texts?.mailSubject ??
    t(`${tKey}.mailTemplateSubject`, {
      currentOrganizationName: currentOrganization?.name || "",
    })
  const resolvedMailBody =
    texts?.mailBody ??
    t(`${tKey}.mailTemplateBody`, {
      currentUserFirstname: currentUser?.firstname || "",
      currentUserLastname: currentUser?.lastname || "",
    })

  const subject = encodeURIComponent(resolvedMailSubject)
  const body = encodeURIComponent(resolvedMailBody)

  const mailtoHref = Array.isArray(contactHref)
    ? `mailto:${contactHref[0]}?cc=${contactHref
        .slice(1)
        .join(",")}&subject=${subject}&body=${body}`
    : `mailto:${contactHref}?subject=${subject}&body=${body}`

  return (
    <div
      className={`AccessModalOverlay${scoped ? " AccessModalOverlay--scoped" : ""}`}
      aria-hidden={false}
    >
      <dialog
        className="AccessModal"
        aria-modal="true"
        aria-labelledby="access-modal-title"
        aria-describedby="access-modal-desc"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="AccessModal__icon" aria-hidden="true">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 9h-1V7a4 4 0 10-8 0v2H7a2 2 0 00-2 2v7a2 2 0 002 2h10a2 2 0 002-2v-7a2 2 0 00-2-2zm-6 7.73V17a1 1 0 112 0v-.27a2 2 0 10-2 0zM9 7a3 3 0 016 0v2H9V7z"
              fill="currentColor"
            />
          </svg>
        </div>
        <h2 id="access-modal-title" className="AccessModal__title">
          {resolvedTitle}
        </h2>
        <p
          id="access-modal-desc"
          className="AccessModal__text"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {resolvedDescription}
        </p>
        <div className="AccessModal__actions">
          {contactHref ? (
            <a
              href={mailtoHref}
              className="AccessModal__button AccessModal__button--primary"
            >
              {resolvedPrimaryButton}
            </a>
          ) : (
            ""
          )}

          {learnMoreHref ? (
            <a
              href={learnMoreHref}
              className="AccessModal__button AccessModal__button--secondary"
              onClick={handleSecondary}
            >
              {resolvedSecondaryButton}
            </a>
          ) : (
            ""
          )}
        </div>
      </dialog>
    </div>
  )
}

AccessModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onRequestAccess: PropTypes.func,
  onLearnMore: PropTypes.func,
  scoped: PropTypes.bool,
  contactHref: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  learnMoreHref: PropTypes.string,
  texts: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    primaryButton: PropTypes.string,
    secondaryButton: PropTypes.string,
    mailSubject: PropTypes.string,
    mailBody: PropTypes.string,
  }),
  translationKey: PropTypes.string,
}

AccessModal.defaultProps = {
  scoped: false,
  contactHref: ["sales@verdikt.io", "helene@verdikt.io"],
}

export default AccessModal
